import {Tokenizer} from 'htmlparser2';
import {last} from 'lodash';

type TagWeights = Record<string, number>;

interface Result {
  result: string;
  isTruncated: boolean;
}

/**
 * Function truncating the input html string efficiently using htmlparser2.
 * The returned string is valid html.
 * Line breaks, whitespace and tabs don't count towards the visibleTextLengthLimit.
 * If the input html is smaller than the limit, the function will the input string.
 * @param html Input html string.
 * @param visibleTextLengthLimit Number of visible characters allowed in the output.
 * @param tagWeights Optional record giving a weight to an html tag in number of characters.
 * This weight counts towards the visibleTextLengthLimit limit.
 * @returns The truncated html string
 */
export function truncateHTMLString(
  html: string,
  visibleTextLengthLimit: number,
  tagWeights?: TagWeights,
): Result {
  // Return early when html is short
  if (html.length <= visibleTextLengthLimit) {
    return {result: html, isTruncated: false};
  }

  let newDirtyHtml = '';
  let textCounter = 0;
  let isTruncated = false;
  let bufferStartIndex = 0;

  const openedTags: string[] = [];

  const processChunk = (startIndex: number, endIndex: number) => {
    if (isTruncated) {
      return;
    }

    newDirtyHtml += html.slice(startIndex, endIndex);
    bufferStartIndex = endIndex;
  };

  const processEligibleTagTextContent = (startIndex: number, endIndex: number) => {
    const trimmedText = html.slice(startIndex, endIndex).trim();
    const {length} = trimmedText;

    if (textCounter + length >= visibleTextLengthLimit) {
      // It is fine to use the trimmed text at the end
      newDirtyHtml += trimmedText.substring(0, visibleTextLengthLimit - textCounter);
      isTruncated = true;
      bufferStartIndex = endIndex;
      // Stop processing the string
      tokenizer.reset();
    } else {
      textCounter += length;
      processChunk(startIndex, endIndex);
    }
  };

  const processText = (startIndex: number, endIndex: number) => {
    if (isTruncated) {
      return;
    }

    const currentTag = last(openedTags);
    const isEligibleTag = isEligibleTagForTextContent(currentTag);
    if (isEligibleTag) {
      processEligibleTagTextContent(startIndex, endIndex);
    } else {
      processChunk(startIndex, endIndex);
    }
  };

  const tokenizer = new Tokenizer(
    {
      // Avoid touching things as much as possible.
      decodeEntities: false,
    },
    {
      onattribdata(_start, endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      onattribentity() {
        // This is called only if the tokenizer decodes entities.
        throw new Error('Unexpected onattribentity callback');
      },
      onattribend(_quote, endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      onattribname(_start, endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      oncdata() {
        // This is called only if the tokenizer is in XML mode.
        throw new Error('Unexpected oncdata callback');
      },
      onclosetag(_start, endIndex) {
        openedTags.pop();
        processChunk(bufferStartIndex, endIndex);
      },
      oncomment(_start, endIndex, _endOffset) {
        processChunk(bufferStartIndex, endIndex);
      },
      ondeclaration(_start, endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      onend() {
        processChunk(bufferStartIndex, html.length);
      },
      onopentagend(endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      onopentagname(start, endIndex) {
        const name = html.slice(start, endIndex);
        openedTags.push(name);

        const tagWeight = tagWeights?.[name];
        if (tagWeight) {
          textCounter += tagWeight;
        }

        processChunk(bufferStartIndex, endIndex);
      },
      onprocessinginstruction(_start, endIndex) {
        processChunk(bufferStartIndex, endIndex);
      },
      onselfclosingtag(endIndex) {
        openedTags.pop();
        processChunk(bufferStartIndex, endIndex);
      },
      ontext(start, endIndex) {
        processChunk(bufferStartIndex, start);
        processText(bufferStartIndex, endIndex);
      },
      ontextentity() {
        // This is called only if the tokenizer decodes entities.
        throw new Error('Unexpected ontextentity callback');
      },
    },
  );

  tokenizer.write(html);
  tokenizer.end();
  tokenizer.reset();

  // Close the leftover tags in case we exited early
  for (let i = openedTags.length - 1; i >= 0; i--) {
    newDirtyHtml += `</${openedTags[i]}>`;
  }

  return {result: newDirtyHtml, isTruncated};
}

function isEligibleTagForTextContent(tagName: string | undefined) {
  return tagName !== 'style' && tagName !== 'script';
}
